/**
 * Sehr kleiner Markdown-Parser fuer das Schwarze Brett.
 *
 * Bewusst ein Parser statt einer HTML-Bibliothek: das Ergebnis sind Tokens, die
 * der Renderer als React-Elemente ausgibt. Damit gibt es kein
 * `dangerouslySetInnerHTML` und keinen XSS-Weg ueber Ankuendigungstexte – wichtig,
 * weil das Session-Cookie nicht HttpOnly ist (IMPLEMENTATION §5).
 */

export type Inline =
  | { type: 'text'; value: string }
  | { type: 'strong'; value: string }
  | { type: 'em'; value: string }
  | { type: 'code'; value: string }
  | { type: 'link'; value: string; href: string };

export type Block =
  | { type: 'heading'; level: 2 | 3; inline: Inline[] }
  | { type: 'paragraph'; inline: Inline[] }
  | { type: 'list'; ordered: boolean; items: Inline[][] };

/** Nur diese Schemata werden als Link gerendert, alles andere bleibt Text. */
const SAFE_LINK = /^(https?:\/\/|mailto:)/i;

const INLINE_PATTERN =
  /(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(`([^`]+)`)|(\[([^\]]+)\]\(([^)\s]+)\))/;

export function parseInline(text: string): Inline[] {
  const out: Inline[] = [];
  let rest = text;

  while (rest.length > 0) {
    const match = INLINE_PATTERN.exec(rest);
    if (!match || match.index === undefined) break;

    if (match.index > 0) out.push({ type: 'text', value: rest.slice(0, match.index) });

    if (match[2] !== undefined) out.push({ type: 'strong', value: match[2] });
    else if (match[4] !== undefined) out.push({ type: 'em', value: match[4] });
    else if (match[6] !== undefined) out.push({ type: 'code', value: match[6] });
    else if (match[8] !== undefined && match[9] !== undefined) {
      const href = match[9];
      if (SAFE_LINK.test(href)) out.push({ type: 'link', value: match[8], href });
      else out.push({ type: 'text', value: match[0] });
    }

    rest = rest.slice(match.index + match[0].length);
  }

  if (rest.length > 0) out.push({ type: 'text', value: rest });
  return mergeText(out.filter((token) => token.type !== 'text' || token.value.length > 0));
}

/**
 * Benachbarte Text-Tokens zusammenfassen. Die entstehen, wenn ein Link-Kandidat
 * verworfen wird (z. B. `[x](javascript:alert(1))` – die URL-Regex stoppt an der
 * ersten Klammer, der Rest kommt als eigenes Text-Token nach).
 */
function mergeText(tokens: Inline[]): Inline[] {
  const merged: Inline[] = [];
  for (const token of tokens) {
    const previous = merged[merged.length - 1];
    if (token.type === 'text' && previous?.type === 'text') {
      merged[merged.length - 1] = { type: 'text', value: previous.value + token.value };
    } else {
      merged.push(token);
    }
  }
  return merged;
}

function listMarker(line: string): { ordered: boolean; content: string } | null {
  const unordered = /^\s*[-*]\s+(.*)$/.exec(line);
  if (unordered?.[1] !== undefined) return { ordered: false, content: unordered[1] };
  const ordered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
  if (ordered?.[1] !== undefined) return { ordered: true, content: ordered[1] };
  return null;
}

export function parseMarkdown(source: string): Block[] {
  const blocks: Block[] = [];
  const lines = source.replace(/\r\n?/g, '\n').split('\n');

  let paragraph: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    blocks.push({ type: 'paragraph', inline: parseInline(paragraph.join(' ')) });
    paragraph = [];
  };
  const flushList = () => {
    if (!list) return;
    blocks.push({
      type: 'list',
      ordered: list.ordered,
      items: list.items.map((item) => parseInline(item)),
    });
    list = null;
  };
  const flushAll = () => {
    flushParagraph();
    flushList();
  };

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();

    if (line.trim() === '') {
      flushAll();
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading?.[1] !== undefined && heading[2] !== undefined) {
      flushAll();
      // h1 gehoert der Seite, Ankuendigungen starten bei h2.
      blocks.push({
        type: 'heading',
        level: heading[1].length === 1 ? 2 : 3,
        inline: parseInline(heading[2]),
      });
      continue;
    }

    const item = listMarker(line);
    if (item) {
      flushParagraph();
      if (list && list.ordered !== item.ordered) flushList();
      if (!list) list = { ordered: item.ordered, items: [] };
      list.items.push(item.content);
      continue;
    }

    flushList();
    paragraph.push(line.trim());
  }

  flushAll();
  return blocks;
}
